import { useMutation, useQuery } from '@apollo/client/react'
import {
  Button,
  Drawer,
  Tabs,
  Separator,
  Grid,
  useToastAlert,
  IOption,
  Caption,
  Marker,
  Skeleton,
  List,
  Card,
  ActionArea,
  MarkerProps,
} from '@design-systems/canvas-ui'
import Icon, { ChevronRightIcon, InfoIcon } from '@design-systems/react-icons'
import { useNavigate } from 'react-router-dom'
import { UserContext } from 'App/UserContext'
import { SEARCH_TEAMS_QUERY } from 'graphql/queries/teams/teamsQueries'
import { useCallback, useContext, useState, useMemo } from 'react'
import { getUserDisplayName } from 'users/utils/userDisplayHelpers'
import UserRolesCombobox from './UserRolesCombobox'
import { SubmitHandler, useController, useForm } from 'react-hook-form'
import { UPDATE_TEAM_MUTATION } from 'graphql/mutations/teams/teamMutations'
import {
  GET_ACCREDITATION_TYPES,
  GET_USER_BY_ID,
} from 'graphql/queries/users/queries/userQueries'
import ControlledFormField from 'components/form/ControlledFormField'
import { UPDATE_USER_ACCREDITATION_MUTATION } from 'graphql/mutations/users/userMutations'
import _ from 'lodash'
import styles from './UserViewDrawer.module.css'

// valid colors pink, orange, yellow, green, blue, purple, grey
// anything not completed or inactive will be yellow by default
const accreditationColorMap: Record<string, MarkerProps['color']> = {
  COMPLETED: 'purple',
  INACTIVE: 'grey',
}

const ACCREDITATION_STATUS = {
  COMPLETED: 'COMPLETED',
  IN_PROGRESS: 'IN_PROGRESS',
  PAUSED: 'PAUSED',
  INACTIVE: 'INACTIVE',
}

const accreditationStatusOptions = [
  {
    // example: IN_PROGRESS -> In Progress
    label: _.capitalize(_.startCase(ACCREDITATION_STATUS.COMPLETED)),
    value: ACCREDITATION_STATUS.COMPLETED,
    id: ACCREDITATION_STATUS.COMPLETED,
  },
  {
    label: _.capitalize(_.startCase(ACCREDITATION_STATUS.IN_PROGRESS)),
    value: ACCREDITATION_STATUS.IN_PROGRESS,
    id: ACCREDITATION_STATUS.IN_PROGRESS,
  },
  {
    label: _.capitalize(_.startCase(ACCREDITATION_STATUS.PAUSED)),
    value: ACCREDITATION_STATUS.PAUSED,
    id: ACCREDITATION_STATUS.PAUSED,
  },
  {
    label: _.capitalize(_.startCase(ACCREDITATION_STATUS.INACTIVE)),
    value: ACCREDITATION_STATUS.INACTIVE,
    id: ACCREDITATION_STATUS.INACTIVE,
  },
]

const ACCREDITATION_EXPLANATIONS = [
  {
    code: 'BPFE',
    description: 'Business Partner Fit Engineers can approve graded sizes.',
  },
  {
    code: 'BPTD',
    description:
      'Business Partner Technical Designers can approve samples and products.',
  },
  {
    code: 'BPPE',
    description:
      'Business Partner Product Engineers can approve samples and products.',
  },
  {
    code: 'BPSA',
    description:
      'Business Partner Spark Approvers can approve samples and products.',
  },
  {
    code: 'BP3D_TECH',
    description:
      'Business Partner 3D Techs can approve 3D samples and products.',
  },
  {
    code: 'SFEL1',
    description: "Can evaluate 'Testing' status.",
  },
  {
    code: 'SFEL2',
    description: "Can evaluate and edit 'Testing' and 'Aesthetic' status.",
  },
]

type Inputs = {
  currentTeamRoles: IOption[]
  currentUserAccreditations: {
    code: string
    description: string
    status: string
  }[]
  activeAccreditation: string | null
  activeAccreditationStatus: string | null
}

const getQueryStringForInternal = (currentUserId: string) => {
  const queryString = `(teamType:INTERNAL OR teamType:EXTERNAL) AND members.userProfileId:${currentUserId}`
  return queryString
}

const getQueryStringForExternal = (
  currentUserId: string,
  partnerId: string,
) => {
  const queryString = `(teamType:INTERNAL OR (teamType:EXTERNAL AND partnerId:${partnerId})) AND members.userProfileId:${currentUserId}`
  return queryString
}

const mapUserAccreditationsToForm = (accreditations: any) => {
  if (!accreditations || accreditations.length === 0) {
    return []
  }
  return accreditations.map((accreditation: any) => ({
    code: accreditation.code,
    description: accreditation.description,
    status: accreditation.status,
  }))
}

type Props = {
  userProfileId: string
  isOpen: boolean
  team?: any
  onRequestClose: () => void
  refetchTeam?: () => Promise<any>
  refetchUser?: () => Promise<any>
}

const UserViewDrawer = ({
  userProfileId,
  isOpen,
  team,
  onRequestClose,
  refetchTeam,
  refetchUser,
}: Props) => {
  const { data, loading, error } = useQuery(GET_USER_BY_ID, {
    variables: { id: userProfileId },
    skip: !isOpen || !userProfileId,
    fetchPolicy: 'network-only',
  })
  const user = data?.getById?.id === userProfileId ? data.getById : null

  if (loading || (!user && !error)) {
    return isOpen ? (
      <Drawer
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        headingText="User details"
      >
        <Skeleton height="700px" />
      </Drawer>
    ) : null
  }

  if (!user) {
    return isOpen ? (
      <Drawer
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        headingText="User details"
      >
        <Caption>There was an error loading this user.</Caption>
      </Drawer>
    ) : null
  }

  return (
    <UserViewDrawerContent
      key={user.id}
      user={user}
      isOpen={isOpen}
      team={team}
      onRequestClose={onRequestClose}
      refetchTeam={refetchTeam}
      refetchUser={refetchUser}
    />
  )
}

type UserViewDrawerContentProps = Omit<Props, 'userProfileId'> & {
  user: any
}

const UserViewDrawerContent = ({
  user,
  isOpen,
  team,
  onRequestClose,
  refetchTeam,
  refetchUser,
}: UserViewDrawerContentProps) => {
  const { isInternal, isAdmin } = useContext(UserContext)
  const makeToast = useToastAlert()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('teams')
  const [hideMainDrawer, setHideMainDrawer] = useState(false)
  const [showAccreditationInfoDrawer, setShowAccreditationInfoDrawer] =
    useState(false)
  const [activeAccreditationRemountKey, setActiveAccreditationRemountKey] =
    useState(0)
  const [
    activeAccreditationStatusRemountKey,
    setActiveAccreditationStatusRemountKey,
  ] = useState(0)

  const handleCloseDrawer = useCallback(() => {
    onRequestClose()
  }, [onRequestClose])

  const teamQueryString = useMemo(() => {
    if (isInternal) {
      return getQueryStringForInternal(user.id)
    } else {
      return getQueryStringForExternal(user.id, user.partnerId)
    }
  }, [isInternal, user.id, user.partnerId])

  const [updateTeam] = useMutation(UPDATE_TEAM_MUTATION)
  const [updateUser] = useMutation(UPDATE_USER_ACCREDITATION_MUTATION)

  const { data: accreditationTypesData, loading: accreditationTypesLoading } =
    useQuery(GET_ACCREDITATION_TYPES, {
      variables: { userType: 'EXTERNAL' },
    })

  const networkAccreditationTypes = useMemo(
    () => accreditationTypesData?.getAccreditationTypes ?? [],
    [accreditationTypesData],
  )
  const isInternalAdmin = isInternal && isAdmin

  // TODO: Implement "infinite scroll" for teams list in the drawer
  const { data, loading, error } = useQuery(SEARCH_TEAMS_QUERY, {
    variables: {
      page: 0,
      q: teamQueryString,
      size: 25,
    },
  })

  const teamsForUser = useMemo(() => {
    return data?.searchTeams?.content || []
  }, [data])

  const handleNavigateToTeam = useCallback(
    (teamId: string) => () => {
      handleCloseDrawer()
      navigate(`/teams/${teamId}`)
    },
    [navigate, handleCloseDrawer],
  )

  const canEditAccreditations = useMemo(() => {
    if (user.companyName === 'Target' || user.partnerId === null) {
      return false
    }
    return true
  }, [user.companyName, user.partnerId])

  const currentUserInTeam = useMemo(() => {
    if (!team || !team.members) {
      return null
    }
    return team.members.find((member: any) => member.id === user.id)
  }, [team, user.id])

  const currentUsersRolesInTeam = useMemo(() => {
    if (!currentUserInTeam || !currentUserInTeam.roles) {
      return []
    }
    return currentUserInTeam.roles.map((role: any) => ({
      id: role.code,
      label: role.description,
      value: role.code,
    }))
  }, [currentUserInTeam])

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty, isValid, isSubmitting },
    setValue,
  } = useForm<Inputs>({
    defaultValues: {
      currentTeamRoles: currentUsersRolesInTeam,
      currentUserAccreditations: mapUserAccreditationsToForm(
        user.accreditations,
      ),
      activeAccreditation: null,
      activeAccreditationStatus: null,
    },
  })

  const currentFormAccreditations = watch('currentUserAccreditations')
  const currentFormActiveAccreditation = watch('activeAccreditation')

  const accreditationTypeOptions = useMemo(() => {
    const assignedCodes = new Set(
      currentFormAccreditations
        .map((accreditation) => accreditation.code)
        .filter((code) => code !== currentFormActiveAccreditation),
    )
    return networkAccreditationTypes
      .filter((type: any) => !assignedCodes.has(type.code))
      .map((type: any) => ({
        value: type.code,
        label: `${type.code} - ${type.description}`,
        id: type.code,
      }))
  }, [
    networkAccreditationTypes,
    currentFormAccreditations,
    currentFormActiveAccreditation,
  ])

  const { field } = useController({
    name: 'currentTeamRoles',
    control,
  })

  const handleRolesSelect = useCallback(
    (selectedRoles: IOption[]) => {
      field.onChange(selectedRoles)
    },
    [field],
  )

  const handleShowAccreditationInfoDrawer = useCallback(() => {
    setHideMainDrawer(true)
    setShowAccreditationInfoDrawer(true)
  }, [])

  const handleHideAccreditationInfoDrawer = useCallback(() => {
    setHideMainDrawer(false)
    setShowAccreditationInfoDrawer(false)
  }, [])

  const handleRemoveAccreditation = useCallback(
    (accreditationCode: string) =>
      (event: React.MouseEvent<HTMLButtonElement>) => {
        // preventing the remove click from also triggering the select click on the marker
        event.stopPropagation()
        const newAccreditations = currentFormAccreditations.filter(
          (accreditation) => accreditation.code !== accreditationCode,
        )
        setValue('currentUserAccreditations', newAccreditations, {
          shouldDirty: true,
        })
        setValue('activeAccreditation', null, { shouldDirty: false })
        setValue('activeAccreditationStatus', null, { shouldDirty: false })
        setActiveAccreditationRemountKey((prevKey) => prevKey + 1)
        setActiveAccreditationStatusRemountKey((prevKey) => prevKey + 1)
      },
    [currentFormAccreditations, setValue],
  )

  const handleSelectAccreditation = useCallback(
    (selectedAccreditationCode: string) => () => {
      const selectedAccreditation = currentFormAccreditations.find(
        (option) => option.code === selectedAccreditationCode,
      )
      if (!selectedAccreditation) {
        return
      }
      setValue('activeAccreditation', selectedAccreditation.code, {
        shouldDirty: false,
      })
      setValue('activeAccreditationStatus', selectedAccreditation.status, {
        shouldDirty: false,
      })
    },
    [currentFormAccreditations, setValue],
  )

  const currentTeamRoles = watch('currentTeamRoles')

  const onRolesSubmit: SubmitHandler<Inputs> = useCallback(
    async (formData) => {
      // mostly a type guard, but also ensures that the team has members before proceeding with the update
      if (!team?.members) {
        makeToast({
          heading: 'Unable to update user',
          message: 'Team information is missing. Please try again.',
          variant: 'error',
        })
        return
      }

      const teamUpdateInput = {
        teamId: team.id,
        teamType: team.teamType,
        members: team.members
          .map((member: any) => {
            if (member.id === user.id) {
              return {
                ...member,
                roles:
                  formData.currentTeamRoles?.map((role) => ({
                    code: role.value,
                    description: role.label,
                  })) || [],
              }
            }
            return member
          })
          .map((member: any) => ({
            userProfileId: member.id,
            roles: member.roles
              .map((role: any) => Number(role.code))
              // it shouldn't be possible to have a non-numeric role code, but this is a safeguard to ensure that the mutation doesn't fail due to invalid data
              .filter((code: number) => Number.isFinite(code)),
          })),
      }

      try {
        await updateTeam({ variables: { teamUpdateInput } })
        makeToast({ heading: 'User updated successfully', variant: 'success' })
      } catch (updateError) {
        console.error('Error updating user:', updateError)
        makeToast({
          heading: 'Unable to update user',
          message:
            'We are unable to update the user at this time. Please try again or contact support.',
          variant: 'error',
        })
        // skip refetching the team if the update failed
        return
      }
      try {
        await refetchTeam?.()
      } catch (refetchError) {
        console.error('Error refetching team:', refetchError)
        makeToast({
          heading: 'Unable to load team',
          message:
            'The team was updated successfully, but view is out of date. Please refresh the page to see the latest team information.',
          variant: 'caution',
        })
      }
      handleCloseDrawer()
    },
    [handleCloseDrawer, makeToast, refetchTeam, team, updateTeam, user],
  )

  const onAccreditationsSubmit: SubmitHandler<Inputs> = useCallback(
    async (formData) => {
      const userUpdateInput = {
        accreditations: formData.currentUserAccreditations.map(
          (accreditation) => ({
            status: accreditation.status,
            type: accreditation.code,
          }),
        ),
      }

      // populate the new accreditation if the user has selected one in the form
      if (formData.activeAccreditation && formData.activeAccreditationStatus) {
        const newAccreditation = {
          status: String(formData.activeAccreditationStatus || ''),
          type: String(formData.activeAccreditation || ''),
        }
        const accreditationsWithoutNew = userUpdateInput.accreditations.filter(
          (accreditation) => accreditation.type !== newAccreditation.type,
        )
        accreditationsWithoutNew.push(newAccreditation)
        userUpdateInput.accreditations = accreditationsWithoutNew
      }

      try {
        await updateUser({
          variables: {
            userProfileId: user.id,
            userUpdates: userUpdateInput,
          },
        })
        makeToast({ heading: 'User updated successfully', variant: 'success' })
      } catch (updateError) {
        console.error('Error updating user:', updateError)
        makeToast({
          heading: 'Unable to update user',
          message:
            'We are unable to update the user at this time. Please try again or contact support.',
          variant: 'error',
        })
        return
      }
      try {
        await refetchUser?.()
      } catch (refetchError) {
        console.error('Error refetching user:', refetchError)
        makeToast({
          heading: 'Unable to load user',
          message:
            'The user was updated successfully, but view is out of date. Please refresh the page to see the latest user information.',
          variant: 'caution',
        })
      }
      handleCloseDrawer()
    },
    [handleCloseDrawer, makeToast, refetchUser, updateUser, user],
  )

  return (
    <>
      {isOpen && !hideMainDrawer && (
        <Drawer
          isOpen={isOpen}
          onRequestClose={handleCloseDrawer}
          headingText="User details"
          footerContent={
            activeTab === 'roles' || activeTab === 'accreditations' ? (
              <Button
                className="hc8-wth-100"
                variant="primary"
                onClick={handleSubmit(
                  activeTab === 'roles'
                    ? onRolesSubmit
                    : onAccreditationsSubmit,
                )}
                isDisabled={!isDirty || !isValid || isSubmitting}
              >
                Save preferences
              </Button>
            ) : null
          }
        >
          <Grid.Container direction="column">
            <Grid.Item size={{ xs: 12 }}>
              <Grid.Container align="center">
                <Grid.Item size={{ xs: true }}>
                  <Caption>
                    <strong>
                      {getUserDisplayName(user.firstName, user.lastName)}
                    </strong>
                  </Caption>
                </Grid.Item>
                <Grid.Item>
                  <Marker color={user.active ? 'green' : 'grey'}>
                    {user.active ? 'Active' : 'Inactive'}
                  </Marker>
                </Grid.Item>
              </Grid.Container>
            </Grid.Item>
            <Grid.Item size={{ xs: 12 }}>
              <Caption below={user.companyName}>{user.email}</Caption>
            </Grid.Item>
            <Grid.Item size={{ xs: 12 }}>
              <Separator />
            </Grid.Item>
            <Grid.Item size={{ xs: 12 }} className="hc8-mv-3x">
              <Tabs activeTab={activeTab} onTabSelect={setActiveTab}>
                <Tabs.Item name="teams">Teams</Tabs.Item>
                {team && user.internal && (
                  <Tabs.Item name="roles">Roles</Tabs.Item>
                )}
                {canEditAccreditations && (
                  <Tabs.Item name="accreditations">Accreditations</Tabs.Item>
                )}
              </Tabs>
            </Grid.Item>
            <Grid.Item
              size={{ xs: 12 }}
              className={`hc8-mv-3x ${styles.scrollContainer}`}
            >
              {activeTab === 'teams' && (
                <Grid.Container direction="column">
                  {loading && (
                    <Grid.Item size={{ xs: 12 }}>
                      <Skeleton height="700px" />
                    </Grid.Item>
                  )}
                  {teamsForUser.length > 0 &&
                    !loading &&
                    teamsForUser.map((usersTeam: any) => (
                      <Grid.Item key={usersTeam.id} size={{ xs: 12 }}>
                        <Card variant="elevated">
                          <ActionArea
                            className="hc8-pa-4x"
                            onClick={handleNavigateToTeam(usersTeam.id)}
                          >
                            <Grid.Item size={{ xs: 12 }}>
                              <Grid.Container>
                                <Grid.Item size={{ xs: true }}>
                                  <Caption>
                                    <strong>{usersTeam.teamName}</strong>
                                  </Caption>
                                </Grid.Item>
                                <Grid.Item>
                                  <Marker
                                    color={
                                      usersTeam.status?.code === 1
                                        ? 'green'
                                        : 'grey'
                                    }
                                  >
                                    {usersTeam.status?.description}
                                  </Marker>
                                </Grid.Item>
                              </Grid.Container>
                            </Grid.Item>
                            <Grid.Item>
                              <Grid.Container align="end">
                                <Grid.Item size={{ xs: true }}>
                                  <Caption
                                    below={
                                      usersTeam.businessPartner?.name ||
                                      'Target'
                                    }
                                  >
                                    {usersTeam.id}
                                  </Caption>
                                </Grid.Item>
                                <Grid.Item>
                                  <Caption>
                                    Go to team <Icon svg={ChevronRightIcon} />
                                  </Caption>
                                </Grid.Item>
                              </Grid.Container>
                            </Grid.Item>
                          </ActionArea>
                        </Card>
                      </Grid.Item>
                    ))}
                  {teamsForUser.length === 0 && !loading && (
                    <Grid.Item size={{ xs: 12 }}>
                      <Caption>There are no teams for this user.</Caption>
                    </Grid.Item>
                  )}
                  {error && (
                    <Grid.Item size={{ xs: 12 }}>
                      <Caption>
                        There was an error loading the teams for this user.
                      </Caption>
                    </Grid.Item>
                  )}
                </Grid.Container>
              )}
              {activeTab === 'roles' && (
                <Grid.Container direction="column">
                  <Grid.Item size={{ xs: 12 }}>
                    {!user.internal && (
                      <>
                        <Caption>Current roles</Caption>
                        {currentTeamRoles.length > 0 ? (
                          <Grid.Container>
                            {currentTeamRoles.map((role) => (
                              <Grid.Item key={role.id}>
                                <Marker color="purple">{role.label}</Marker>
                              </Grid.Item>
                            ))}
                          </Grid.Container>
                        ) : (
                          <Caption>No team roles assigned.</Caption>
                        )}
                      </>
                    )}
                    {user.internal && (
                      <UserRolesCombobox
                        label="Current team roles"
                        placeholder="Team roles"
                        onRolesSelect={handleRolesSelect}
                        value={currentTeamRoles}
                      />
                    )}
                  </Grid.Item>
                </Grid.Container>
              )}
              {activeTab === 'accreditations' && (
                <Grid.Container direction="column">
                  <Grid.Item size={{ xs: 12 }}>
                    <Grid.Container align="center">
                      <Grid.Item size={{ xs: true }}>
                        <Caption>Current accreditations</Caption>
                      </Grid.Item>
                      <Grid.Item>
                        <Button
                          variant="ghost"
                          onClick={handleShowAccreditationInfoDrawer}
                        >
                          <Icon svg={InfoIcon} />
                          About accreditations
                        </Button>
                      </Grid.Item>
                    </Grid.Container>
                  </Grid.Item>
                  <Grid.Item size={{ xs: 12 }}>
                    {currentFormAccreditations.length > 0 ? (
                      <Grid.Container>
                        {currentFormAccreditations.map((accreditation: any) => (
                          <Grid.Item key={accreditation.code}>
                            <Marker
                              onRemove={
                                isInternal
                                  ? handleRemoveAccreditation(
                                      accreditation.code,
                                    )
                                  : undefined
                              }
                              onClick={
                                isInternal
                                  ? handleSelectAccreditation(
                                      accreditation.code,
                                    )
                                  : undefined
                              }
                              color={
                                (accreditationColorMap[accreditation.status] ||
                                  'yellow') as MarkerProps['color']
                              }
                            >
                              {accreditation.code}
                            </Marker>
                          </Grid.Item>
                        ))}
                      </Grid.Container>
                    ) : (
                      <Caption>No accreditations assigned.</Caption>
                    )}
                  </Grid.Item>
                  {isInternalAdmin && (
                    <>
                      <Grid.Item size={{ xs: 12 }}>
                        {!accreditationTypesLoading && (
                          <ControlledFormField
                            key={activeAccreditationRemountKey}
                            control={control}
                            name="activeAccreditation"
                            id="activeAccreditation"
                            label="Add or edit an accreditation"
                            type="select"
                            options={accreditationTypeOptions}
                          />
                        )}
                        {accreditationTypesLoading && <Skeleton />}
                      </Grid.Item>
                      <Grid.Item>
                        <ControlledFormField
                          key={activeAccreditationStatusRemountKey}
                          control={control}
                          name="activeAccreditationStatus"
                          id="activeAccreditationStatus"
                          label="Accreditation status"
                          type="select"
                          options={accreditationStatusOptions}
                          hintText='Accreditation type will display when the status is "Completed".'
                        />
                      </Grid.Item>
                    </>
                  )}
                </Grid.Container>
              )}
            </Grid.Item>
          </Grid.Container>
        </Drawer>
      )}
      {showAccreditationInfoDrawer && (
        <Drawer
          isOpen={showAccreditationInfoDrawer}
          onRequestClose={handleHideAccreditationInfoDrawer}
          headingText="About accreditations"
        >
          <Grid.Container direction="column">
            <Grid.Item size={{ xs: 12 }}>
              <Caption>
                This feature enables certified and trained vendors to approve
                samples and products. Please contact your administrator to learn
                more about accreditation.
              </Caption>
            </Grid.Item>
            <Grid.Item size={{ xs: 12 }}>
              <List>
                {ACCREDITATION_EXPLANATIONS.map((accreditation) => (
                  <List.Item key={accreditation.code}>
                    <Caption below={accreditation.description}>
                      <strong>{accreditation.code}</strong>
                    </Caption>
                  </List.Item>
                ))}
              </List>
            </Grid.Item>
          </Grid.Container>
        </Drawer>
      )}
    </>
  )
}

export default UserViewDrawer
